/**
 * Modal de cadastro de paciente — usado na página Pacientes e inline na
 * Dispensação (quando a busca não encontra). CPF aceita pontuação e envia
 * só dígitos (API valida 11).
 */
import { useState } from "react";

import { useCriarPaciente } from "@/hooks/useApi";
import type { Paciente } from "@/types/api";

interface Props {
  nomeInicial?: string;
  onCreated: (p: Paciente) => void;
  onClose: () => void;
}

export function PacienteFormModal({ nomeInicial = "", onCreated, onClose }: Props) {
  const criar = useCriarPaciente();
  const [nome, setNome] = useState(nomeInicial);
  const [cpf, setCpf] = useState("");
  const [prontuario, setProntuario] = useState("");
  const [nascimento, setNascimento] = useState("");
  const [convenio, setConvenio] = useState("");
  const [carteirinha, setCarteirinha] = useState("");

  const cpfDigitos = cpf.replace(/\D/g, "");
  const ok = nome.trim().length >= 2 && cpfDigitos.length === 11 && prontuario.trim().length >= 1;

  async function salvar() {
    if (!ok) return;
    try {
      const p = await criar.mutateAsync({
        nome: nome.trim(),
        cpf: cpfDigitos,
        numero_prontuario: prontuario.trim(),
        data_nascimento: nascimento || null,
        convenio: convenio.trim() || null,
        numero_carteirinha: carteirinha.trim() || null,
      });
      onCreated(p);
    } catch { /* erro via criar.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">Cadastrar paciente</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Nome completo</label>
          <input className="input mt-1" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">CPF</label>
            <input
              className="input mt-1" inputMode="numeric" placeholder="000.000.000-00"
              value={cpf} onChange={(e) => setCpf(e.target.value)}
            />
            {cpf && cpfDigitos.length !== 11 ? (
              <p className="mt-0.5 text-xs text-anarosa">CPF precisa de 11 dígitos ({cpfDigitos.length}/11)</p>
            ) : null}
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Nº prontuário</label>
            <input className="input mt-1" value={prontuario} onChange={(e) => setProntuario(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Nascimento (opc.)</label>
            <input className="input mt-1" type="date" value={nascimento} onChange={(e) => setNascimento(e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Convênio (opc.)</label>
            <input className="input mt-1" value={convenio} onChange={(e) => setConvenio(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Nº carteirinha (opc.)</label>
          <input className="input mt-1" value={carteirinha} onChange={(e) => setCarteirinha(e.target.value)} />
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!ok || criar.isPending} onClick={salvar}>
            {criar.isPending ? "Salvando…" : "Cadastrar"}
          </button>
        </div>
        {criar.error ? (
          <p className="text-sm text-anarosa">{(criar.error as Error).message}</p>
        ) : null}
      </div>
    </div>
  );
}
